/**
 * MobileBreakpointContext — single-subscription provider for viewport
 * breakpoint detection. Replaces the per-call-site `matchMedia` listener
 * that `useIsMobile` used to attach, which produced 17+ independent
 * listeners on the /library cold load (one per consumer).
 *
 * Usage:
 *   <MobileBreakpointProvider breakpoints={[768, 1280]}>
 *     <YourSubtree />
 *   </MobileBreakpointProvider>
 *
 * Consumers call `useIsMobile()` (default 768px) or `useIsMobile(1280)`
 * with no source change required — the hook delegates to this context.
 *
 * If no provider is in the tree (SSR, isolated tests), the hook falls
 * back to `false`. That mirrors `useIsMobile`'s prior SSR behavior:
 * the previous implementation also returned `false` until the first
 * effect ran, so subtrees that depend on `isMobile` for layout still
 * render the desktop variant first and switch on hydration.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

interface MobileBreakpointContextValue {
  /**
   * Map of `breakpoint -> matches`. A `true` value means the viewport is
   * BELOW the breakpoint (i.e., `(max-width: ${bp - 1}px)` matched).
   */
  matches: Readonly<Record<number, boolean>>
}

const MobileBreakpointContext = createContext<MobileBreakpointContextValue | null>(null)

/**
 * Default breakpoint set used by the Community surface. 768 covers the
 * mobile/desktop split most consumers care about; 1280 is used by
 * `SystemPlaylistFullView` for its three-step responsive switch.
 *
 * Frozen + module-scoped so the reference is stable across renders — the
 * provider's subscribe effect keys off this identity.
 */
export const COMMUNITY_BREAKPOINTS: ReadonlyArray<number> = Object.freeze([768, 1280])

export interface MobileBreakpointProviderProps {
  /**
   * Breakpoints to track. The provider opens one `matchMedia` listener per
   * unique value. Listing every breakpoint your subtree needs here lets the
   * provider eagerly subscribe and avoids any runtime registration race.
   *
   * Today the Community surface uses 768 (mobile) and 1280 (tablet/below).
   */
  breakpoints: ReadonlyArray<number>
  children?: React.ReactNode
}

export function MobileBreakpointProvider ({
  breakpoints,
  children,
}: MobileBreakpointProviderProps) {
  // Map from breakpoint -> match boolean. Seeded `false` for SSR; the
  // mount effect below populates real values from `matchMedia`.
  const [matches, setMatches] = useState<Record<number, boolean>>(() => {
    const seed: Record<number, boolean> = {}
    breakpoints.forEach((bp) => { seed[bp] = false })
    return seed
  })

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined
    }

    // One MediaQueryList per breakpoint. Each gets its own `change` handler
    // that updates the corresponding slot of the matches map.
    type MqlEntry = {
      bp: number
      mql: MediaQueryList
      handler: (e: MediaQueryListEvent) => void
    }
    const mqls: MqlEntry[] = []
    const initial: Record<number, boolean> = {}

    breakpoints.forEach((bp) => {
      const mql = window.matchMedia(`(max-width: ${bp - 1}px)`)
      initial[bp] = mql.matches
      const handler = (e: MediaQueryListEvent) => {
        setMatches((prev) => {
          if (prev[bp] === e.matches) return prev
          return { ...prev, [bp]: e.matches }
        })
      }
      mql.addEventListener('change', handler)
      mqls.push({ bp, mql, handler })
    })

    setMatches((prev) => ({ ...prev, ...initial }))

    return () => {
      mqls.forEach(({ mql, handler }) => {
        mql.removeEventListener('change', handler)
      })
    }
    // Effect re-runs only if the set of breakpoints changes. Compare via
    // sorted-stringified key so an inline-literal `breakpoints={[768, 1280]}`
    // doesn't cause re-subscription on every parent render.
  }, [breakpoints.slice().sort().join(',')])

  // Value object is memoized so context consumers don't see identity churn
  // when unrelated state in this component changes. The map itself is the
  // setState target so it has a fresh reference only on actual transitions.
  const value = useMemo<MobileBreakpointContextValue>(() => ({ matches }), [matches])

  return (
    <MobileBreakpointContext.Provider value={value}>
      {children}
    </MobileBreakpointContext.Provider>
  )
}

/**
 * Read a breakpoint's match state from the surrounding provider. Returns
 * `false` if no provider is in the tree (SSR, isolated tests) OR if the
 * requested breakpoint wasn't included in the provider's `breakpoints`
 * prop. In dev mode the latter case logs a one-time warning.
 */
export function useMobileBreakpoint (breakpoint: number): boolean {
  const ctx = useContext(MobileBreakpointContext)
  if (!ctx) return false
  if (process.env.NODE_ENV !== 'production' && !(breakpoint in ctx.matches)) {
    // eslint-disable-next-line no-console
    console.warn(
      `[useMobileBreakpoint] breakpoint ${breakpoint} not registered with `
      + 'MobileBreakpointProvider. Add it to the provider\'s `breakpoints` prop.',
    )
  }
  return ctx.matches[breakpoint] ?? false
}
